import React from 'react';
import styled from 'styled-components';
import { Typography } from 'antd';
import RecentRegistrations from './RecentRegistrations';
import Tournaments from './Tournaments';
import { TeamOutlined } from '@ant-design/icons';
import {
  COLORS,
  DateFormatStrings,
  FONT_WEIGHTS,
  TEXT_SIZES,
} from '../../../utils/constants';
import { useEventContext } from '../../../contexts/EventContext';
import { convertUtcToLocal } from '../../../utils/dateUtils';

const { Text } = Typography;

const EventDetailsScreen: React.FC = () => {
  const { eventData, refreshEventData } = useEventContext();

  if (!eventData) {
    return null;
  }

  const eventId = eventData.eventId;

  return (
    <Wrapper>
      <Content>
        <Left>
          <EventCard>
            <EventImage
              src={eventData?.attachmentUrl ?? '/fallback.png'}
              alt="event"
            />
            <EventBody>
              <EventTitle>{eventData?.eventName ?? '--'}</EventTitle>
              <EventDates>
                {eventData?.eventStartDate && eventData?.eventEndDate ? (
                  <>
                    {convertUtcToLocal(
                      eventData.eventStartDate,
                      DateFormatStrings.DD_MMM_YYYY,
                    )}{' '}
                    -{' '}
                    {convertUtcToLocal(
                      eventData.eventEndDate,
                      DateFormatStrings.DD_MMM_YYYY,
                    )}
                  </>
                ) : (
                  '--'
                )}
              </EventDates>
              <Row>
                <InfoBox>
                  <IconWrapper
                    bg={COLORS.grey.secondaryLight}
                    color={COLORS.blue.medium}
                  >
                    <TeamOutlined style={{ fontSize: 18 }} />
                  </IconWrapper>
                  <div>
                    <InfoValue>{eventData?.totalParticipants ?? 0}</InfoValue>
                    <InfoLabel>Participants</InfoLabel>
                  </div>
                </InfoBox>
                <InfoBox>
                  <IconWrapper
                    bg={COLORS.grey.secondaryLight}
                    color={COLORS.green.secondary}
                  >
                    <RupeeSpan>₹</RupeeSpan>
                  </IconWrapper>
                  <div>
                    <InfoValue>₹{eventData?.totalRevenue ?? 0}</InfoValue>
                    <InfoLabel>Revenue</InfoLabel>
                  </div>
                </InfoBox>
              </Row>
            </EventBody>
          </EventCard>
          <RecentContainer>
            <RecentRegistrations eventId={eventId} />
          </RecentContainer>
        </Left>
        <Right>
          <Tournaments
            tournaments={eventData?.tournamentSummaries ?? []}
            total={eventData?.totalTournaments ?? 0}
            eventDetails={eventData}
            reloadEvent={refreshEventData}
          />
        </Right>
      </Content>
    </Wrapper>
  );
};

export default EventDetailsScreen;

/* styles */
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`;

const Content = styled.div`
  display: flex;
  gap: 20px;
  height: 100%;
  flex: 1;
  overflow-y: auto;
`;

const Left = styled.div`
  height: 100%;
  width: 40%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const EventCard = styled.div`
  background: ${COLORS.white.primary};
  border-radius: 16px;
  overflow: hidden;
  border: 0.5px solid ${COLORS.grey.lightMedium};
  display: flex;
  flex-direction: column;
  height: 60%;
  flex-shrink: 0;
`;

const EventImage = styled.img`
  width: 100%;
  height: 45%;
  object-fit: contain;
  flex-shrink: 0;
  display: block;
  background: ${COLORS.grey.extraLight};

  /* Small screens */
  @media (max-height: 768px) {
    height: 45%;
  }

  /* Medium to large screens */
  @media (min-height: 769px) and (max-height: 1080px) {
    height: 56%;
  }

  /* Very large screens */
  @media (min-height: 1081px) {
    height: 66%;
  }
`;

const EventBody = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
`;

const EventTitle = styled(Text)`
  font-size: ${TEXT_SIZES.LG}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  color: ${COLORS.black.primary};
`;

export const EventDates = styled(Text)`
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  color: ${COLORS.grey.medium};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconWrapper = styled.div<{ bg: string; color: string }>`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${({ bg }) => bg};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 12px;
  color: ${({ color }) => color};
`;

const InfoBox = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const InfoValue = styled.div`
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
  margin-top: 4px;
  color: ${COLORS.black.primary};
`;

const InfoLabel = styled.div`
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  color: ${COLORS.grey.medium};
`;

const RupeeSpan = styled.span`
  font-size: ${TEXT_SIZES.BASE}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
`;

const RecentContainer = styled.div`
  flex: 1;
  padding-right: 6px;
  height: 40%;
`;

const Right = styled.div`
  width: 60%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
